import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';
import { AnimationConfigs } from '../powerOfGodsCore/constants/globalConstants';
import { SlotIcon } from '../../../core/classes/slotIcon';
import {
  iconStates,
  reelParams,
  reelSymbolIndexesConfig,
} from '../powerOfGodsCore/constants/reelConstants';

export class ReelView extends PIXI.Container {
  index = 0;

  reelContainer = null;

  winContainer = null;

  iconContainers = [];

  icons = [];

  visibleIconIndex = null;

  iconsHeight = 0;

  iconsSpacing = 0;

  iconsInReel = 0;

  spacing = 0;

  visibleRows = 0;

  speedDuration = 0;

  onInitCallback = null;

  duration = 0;

  winSymbolAnimation = null;

  iconKeysBeforeBonusTransition = [];

  constructor(reelContainer, winContainers) {
    super();
    this.index = 0;
    this.reelContainer = reelContainer;
    this.winContainer = winContainers;
    const params = reelParams;
    this.iconsHeight = params.iconsHeight;
    this.iconsSpacing = params.iconsSpacing;
    this.visibleRows = params.visibleRows;
    this.iconsInReel = params.iconsInReel;
    this.spacing = params.spacing;
    this.speedDuration = params.speedDuration;
    this.visibleIconIndex = Math.floor(this.iconsInReel / 2);
  }

  onInit(cb) {
    this.onInitCallback = cb;
  }

  initContainer() {
    const params = reelParams;
    let x = this.index * (params.iconsWidth + (this.index === 0 ? 0 : this.spacing));
    x += params.offsetForContainerBoardWidth;
    this.x = x;
    this.x = 0;
    this.startPositionY =
      -(this.iconsHeight + this.iconsSpacing) * (this.visibleRows * 3) - this.iconsSpacing * 2;
    this.y = this.startPositionY;

    params.finalPositionX = x;
    params.finalPositionY = -this.iconsHeight * (this.visibleRows - 1);
    params.finalStopPositionY = this.startPositionY;
    this.reelContainer.addChild(this);
    if (this.onInitCallback) this.onInitCallback();
  }

  async addIcons(isInBonusScene, responeData) {
    this._createIconContainers();
    this.iconContainers.forEach((container, i) => {
      const icon = new SlotIcon({
        parent: container,
        elementId: `spinIcon_${this.index}_${i + 1}`,
        rowIndex: i,
        winParent: this.winContainer,
        styles: { x: 0, y: i * (this.iconsHeight + this.iconsSpacing) },
        reelIndex: this.index,
        index: 0,
        key: responeData,
      });
      container.addChild(icon);
      this.icons.push(icon);
    });
  }

  getRandomSymbol(isForBonus = false) {
    const [min, max] = isForBonus
      ? reelSymbolIndexesConfig.bonus
      : reelSymbolIndexesConfig.standard;
    return min + Math.floor(Math.random() * (max - min + 1));
  }

  _createIconContainers() {
    for (let i = 0; i < this.iconsInReel; i += 1) {
      const c = new PIXI.Container();
      c.name = 'iconContainer';
      this.iconContainers.push(c);
      this.addChild(c);
    }
  }

  playLoop({ posStart, posEnd, duration }, onRepeat, isForBonus) {
    this.setPosition(posStart);
    this.setRandomSymbolsOnStart(isForBonus);
    if (this.anim) this.anim.kill();
    this.anim = gsap.to(this, {
      pixi: { y: posEnd },
      duration,
      repeat: -1,
      ease: 'linear',
      onRepeat,
    });
  }

  setRandomSymbolsOnStart(isForBonus) {
    const copySymbol = this.icons[this.visibleIconIndex];
    copySymbol.changeToActualParent();
    this.icons[this.icons.length - 1].changeIcon(copySymbol.key, iconStates.idle);
    this.icons.forEach((icon, index) => {
      if (index < this.icons.length - 1) {
        this.icons[index].changeIcon(this.getRandomSymbol(isForBonus), iconStates.blur);
      }
    });
  }

  setPosition(posY) {
    this.y = posY;
  }

  playStop({ posStart, posEndStop, stopDuration, ease }, onComplete, isTurbo, data) {
    if (this.anim) this.anim.kill();
    this.setResponseSymbol(data);
    if (isTurbo) {
      this.setPosition(posStart);
    }
    gsap.to(this, {
      pixi: { y: posEndStop },
      duration: stopDuration,
      ease: ease,
      onComplete,
    });
  }

  playStretchStop({ posEndStop, stretchDuration, ease }, onComplete, data) {
    if (this.anim) this.anim.kill();
    this.setResponseSymbol(data);
    gsap.to(this, {
      pixi: { y: posEndStop },
      duration: stretchDuration,
      ease,
      onComplete,
    });
  }

  setResponseSymbol(data) {
    this.icons[this.visibleIconIndex].changeIcon(data, iconStates.blur);
  }

  changeToBonusMode() {
    this.icons.forEach((symbol) => {
      this.iconKeysBeforeBonusTransition.push(symbol.key);
      const randomBonusKeyForSymbols = this.getRandomSymbol(true);
      symbol.changeState(iconStates.idle);
      symbol.changeIcon(randomBonusKeyForSymbols);
    });
  }

  changeToNormalMode() {
    this.icons.forEach((symbol, index) => {
      symbol.changeState(iconStates.idle);
      symbol.changeIcon(this.iconKeysBeforeBonusTransition[index]);
    });
    this.iconKeysBeforeBonusTransition = [];
  }

  skipWinActions() {
    return new Promise((resolve) => {
      const symbol = this.winSymbolAnimation;
      const anim = symbol.icon;
      if (symbol.currentState === iconStates.win) {
        anim.goToAnimationEnd(iconStates.win);
        const onComplete = () => {
          anim.removeListener('complete', onComplete);
          resolve();
        };
        anim.addListener('complete', onComplete, this, iconStates.win, true);
      } else {
        resolve();
      }
    });
  }

  startWinAnimation() {
    const symbol = this.icons[this.visibleIconIndex];
    this.winSymbolAnimation = this.icons[this.visibleIconIndex];
    symbol.changeState(iconStates.win);

    return new Promise((resolveWin) => {
      const onWinComplete = () => {
        symbol.icon.removeListener('complete', onWinComplete);
        symbol.icon.play(iconStates.idle, true);
        symbol.currentState = iconStates.idle;
        resolveWin();
      };
      symbol.icon.addListener('complete', onWinComplete);
    });
  }

  calculatePositionsDuration(isTurbo) {
    const startY = this.startPositionY;
    const rowsH = this.visibleRows * 3 * this.iconsHeight;
    if (isTurbo) {
      return {
        posStart: startY - rowsH,
        posEnd: startY + rowsH,
        duration: this.speedDuration,
        stopDuration: this.speedDuration / 3,
        stopEase: 'power1.in',
        posEndStop: startY,
      };
    }
    return {
      posStart: startY - rowsH,
      posEnd: startY + rowsH,
      duration: this.speedDuration,
      stopDuration: this.speedDuration,
      stopEase: AnimationConfigs.stopNormal,
      bonusEase: AnimationConfigs.stopNormal,
      posEndStop: startY,
    };
  }

  updateLoopSymbols([start, end], isForBonus) {
    const copyIconKey = this.icons[0].key;

    for (let i = start; i < end; i += 1) {
      const randomKey = this.getRandomSymbol(isForBonus);
      this.icons[i].changeIcon(randomKey, iconStates.blur);
    }

    const lastIdx = this.icons.length - 1;
    this.icons[lastIdx].changeIcon(copyIconKey, iconStates.blur);
  }

  setSymbolsIdle() {
    this.icons.forEach((icon) => icon.changeState(iconStates.blur));
  }
}
